/**
 * CancelInstanceButton — anulowanie aktywnej instancji procesu (F17 T17.1.7).
 *
 * Self-contained: z `{ moduleCode, recordId }` wykrywa aktywną instancję
 * (`useActiveInstanceForRecord`). Widoczny TYLKO gdy:
 *   (a) jest aktywna instancja dla rekordu, ORAZ
 *   (b) user ma permission `processes.instances.cancel` w firmie REKORDU (super-user bypassuje).
 *
 * Firma rekordu, nie firma aktywna w aplikacji (decyzja właściciela 2026-10-08: „firma z rekordu,
 * nie ze sprawy"): flagę `canCancel` daje serwer w odpowiedzi listingu spraw rekordu
 * (`recordPermissions`, ten sam klucz zapytania co baner — bez dodatkowego żądania), więc faktura
 * firmy B otwarta przy wybranej firmie A liczy się wg B. Bez odpowiedzi (ładowanie, błąd) = brak
 * prawa, przycisk ukryty. Prawo i tak sprawdza serwer przy kliknięciu — flaga służy tylko
 * widoczności.
 *
 * Potwierdzenie przez `<ConfirmDialog variant="danger">` (CLAUDE.md §4.12 — zero
 * window.confirm). Mutation → `POST /api/processes/instances/:id/cancel`.
 *
 * Rekord sprawy usunięty (serwer: 404 `TARGET_RECORD_DELETED` — sprawa „nie istnieje" do
 * przywrócenia kartoteki, nic nie zmieniono) → toast „Nie znaleziono kartoteki.", zamknięcie
 * potwierdzenia i odświeżenie danych procesów. Tak samo przy module kartoteki sprawy wyłączonym
 * po otwarciu okna (serwer: 402 `TARGET_MODULE_INACTIVE`) — toast „Moduł tej kartoteki jest
 * nieaktywny.".
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useActiveInstanceForRecord } from '@/hooks/useActiveInstanceForRecord';
import {
  isTargetModuleInactiveError,
  isTargetRecordDeletedError,
  TARGET_MODULE_INACTIVE_I18N_KEY,
  TARGET_RECORD_DELETED_I18N_KEY,
} from '@/lib/processes/approvals-api';
import type { InstanceStatus } from '@/lib/processes/instance-api';
import { cancelInstance } from '@/lib/processes/instance-api';

export interface CancelInstanceButtonProps {
  moduleCode: string;
  recordId: string;
  /**
   * Instancja do anulowania. Gdy podana — celujemy w nią (także ZAKOŃCZONĄ: reset
   * slotu, T17.1.8b); gdy brak — fallback na żywą instancję z hooka.
   */
  instanceId?: string;
  /** Status celowanej instancji — steruje treścią potwierdzenia (zakończona → „unieważnij"). */
  instanceStatus?: InstanceStatus;
  /** Wywołane po pomyślnym anulowaniu (np. refetch karty rekordu). */
  onCancelled?: () => void;
  testIdPrefix?: string;
}

export function CancelInstanceButton({
  moduleCode,
  recordId,
  instanceId,
  instanceStatus,
  onCancelled,
  testIdPrefix = 'cancel-instance',
}: CancelInstanceButtonProps): ReactElement | null {
  const { t } = useTranslation();
  const { show } = useToast();
  const { me } = useTenantAuth();
  const queryClient = useQueryClient();
  const { instance, recordPermissions } = useActiveInstanceForRecord(moduleCode, recordId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Prop ma pierwszeństwo (baner podaje instancję bieżącą LUB zakończoną do resetu);
  // bez propa — fallback na żywą instancję z hooka.
  const targetId = instanceId ?? instance?.instance.id ?? null;
  const targetStatus = instanceStatus ?? instance?.instance.status ?? null;
  const isFinished = targetStatus === 'completed' || targetStatus === 'error';

  // Prawo anulowania w firmie REKORDU (flaga z serwera), nie firmy aktywnej — patrz nagłówek pliku.
  const canCancel = !!me && (me.user.isSuperUser || recordPermissions?.canCancel === true);

  const mutation = useMutation({
    mutationFn: () => {
      // Guard zamiast `instance!`: przycisk znika z DOM bez instancji, ale jawny
      // błąd jest bezpieczniejszy niż non-null assertion (i NIE woła onSuccess).
      if (!targetId) {
        // Kod-identyfikator (nie tekst UI) — błąd dev-facing, użytkownik widzi toast z t().
        return Promise.reject(new Error('NO_INSTANCE'));
      }
      return cancelInstance(targetId);
    },
    onSuccess: () => {
      show({
        variant: 'success',
        title: t('tenant.processes.cancel.done', 'Proces anulowany'),
      });
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['processes'] });
      onCancelled?.();
    },
    onError: (err: unknown) => {
      if (isTargetRecordDeletedError(err)) {
        // Rekord sprawy usunięty (serwer: 404 `TARGET_RECORD_DELETED`) — sprawa „nie istnieje",
        // nic nie zostało zmienione. Zamykamy potwierdzenie i odświeżamy dane procesów.
        show({ variant: 'error', title: t(TARGET_RECORD_DELETED_I18N_KEY) });
        setConfirmOpen(false);
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
        return;
      }
      if (isTargetModuleInactiveError(err)) {
        // Moduł kartoteki sprawy wyłączony po otwarciu okna (serwer: 402 `TARGET_MODULE_INACTIVE`) —
        // nic nie zostało zmienione, a serwer ukrywa taką sprawę na listach. Zamykamy potwierdzenie
        // i odświeżamy dane procesów, tak jak przy usuniętym rekordzie.
        show({ variant: 'error', title: t(TARGET_MODULE_INACTIVE_I18N_KEY) });
        setConfirmOpen(false);
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
        return;
      }
      show({
        variant: 'error',
        title: t('tenant.processes.cancel.error', 'Nie udało się anulować procesu'),
      });
    },
  });

  if (!targetId || !canCancel) return null;

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        data-testid={`${testIdPrefix}-btn`}
        onClick={() => setConfirmOpen(true)}
        className="border-red-500 text-red-600 hover:bg-red-500/10 active:bg-red-500/20"
      >
        {t('tenant.processes.cancel.label', 'Anuluj proces')}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        variant="danger"
        title={
          isFinished
            ? t('tenant.processes.cancel.titleFinished', 'Unieważnić zakończony proces?')
            : t('tenant.processes.cancel.title', 'Anulować proces?')
        }
        message={
          isFinished
            ? t(
                'tenant.processes.cancel.messageFinished',
                'Zakończony proces zostanie unieważniony (przejdzie w stan anulowany), a kartoteka zwolniona — będzie można uruchomić proces od nowa. Tej operacji nie można cofnąć.',
              )
            : t(
                'tenant.processes.cancel.message',
                'Ta sprawa zostanie anulowana. Tej operacji nie można cofnąć.',
              )
        }
        confirmLabel={
          isFinished
            ? t('tenant.processes.cancel.confirmFinished', 'Unieważnij')
            : t('tenant.processes.cancel.confirm', 'Anuluj proces')
        }
        // „Wróć" zamiast domyślnego „Anuluj" — obok „Anuluj proces" dwa „Anuluj" myliły się
        // (decyzja właściciela 2026-10-06).
        cancelLabel={t('tenant.processes.cancel.back', 'Wróć')}
        onConfirm={() => mutation.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
